import { Task } from '@spike/task-model';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import { FunctionComponent, MouseEvent, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import clsx from 'clsx';
import styles from './TasksTableRow.module.scss';
import TaskInfo from './UI/TaskInfo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { completeTaskThunk, uncompleteTaskThunk } from '@spike/tasks-action';
import { useApiClientWrapper, useTimeZone } from 'hooks';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import ListPetInfo from 'components/Bookings2/UI/ListPetInfo/ListPetInfo';
import { taskToPetListItem } from './mappers/TaskToPetListItem';
import { faCircleCheck, faPen, faRotateLeft, faTrashCan } from '@fortawesome/pro-light-svg-icons';
import { faLocationDot } from '@fortawesome/pro-regular-svg-icons';
import { GenericDeleteModal } from 'components/UI/V3/Modals/DeleteModal';
import { NewBookingsStatus, singleRescheduleThunk } from '@spike/new-bookings-action';
import useNonInitialEffect from '@versiondos/hooks';
import { AppointmentsStatus, BookingsStatus, getAppointmentThunk } from '@spike/bookings-action';
import Appointment from '@spike/appointment-model';
import TaskComponent from '../Task/TaskComponent';
import { TaskTypeSelector } from '../TaskTypeSelector/TaskTypeSelector';
import { getOrderByAppointmentThunk, OrdersStatus } from '@spike/orders-action';
import { Order } from '@spike/order-model';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import OrderDetail from 'components/OrderDetail';
import { careOf } from '../careTaskTypes';

interface TasksTableRowProps {
    task: Task;
    roomLabel?: { room: string; unit: string };
    showLocationColumn?: boolean;
    className?: string;
    variant?: string;
    previewActions?: boolean;
}

export const TasksTableRow: FunctionComponent<TasksTableRowProps> = props => {
    const isMobile = useMediaQuery({ query: '(max-width:600px)' });
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const isCompleted = props.task.status === 'completed';
    const masterData = useSelector((state: RootState) => state.masterData.data);
    const timezone = useTimeZone();
    const [taskModalKey, setTaskModalKey] = useState(0);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isLoadingActions, setIsLoadingActions] = useState(false);
    const [isLoadingTaskComponent, setIsLoadingTaskComponent] = useState(false);
    const [showTaskComponent, setShowTaskComponent] = useState(false);
    const [showOrderDetail, setShowOrderDetail] = useState(false);
    const [pendingBookingDetail, setPendingBookingDetail] = useState(false);
    const [showTaskTypeSelector, setShowTaskTypeSelector] = useState(false);
    const [selectedTaskType, setSelectedTaskType] = useState<string | undefined>(undefined);
    const [taskToEdit, setTaskToEdit] = useState<Task | undefined>(undefined);
    const newBookingsStatus = useSelector<RootState, NewBookingsStatus>(state => state.newBookings.status);
    const bookingsStatus = useSelector<RootState, BookingsStatus>(state => state.bookings.status);
    const appointmentsStatus = useSelector<RootState, AppointmentsStatus>(state => state.appointments.status);
    const appointment = useSelector<RootState, Appointment | undefined>(state => state.appointments.appointment);
    const order = useSelector<RootState, Order | undefined>(state => state.orders.order);
    const orderStatus = useSelector<RootState, OrdersStatus>(state => state.orders.status);
    const { getOrganizationMarketplace, isUserLoggedInMarketplace } = useOrganizationMarketplaces();

    useNonInitialEffect(() => {
        switch (newBookingsStatus) {
            case NewBookingsStatus.SingleRescheduling:
                setIsLoadingActions(true);
                break;
            case NewBookingsStatus.SingleRescheduleSuccess:
                setShowDeleteModal(false);
                setIsLoadingActions(false);
                break;
            case NewBookingsStatus.Error:
                setShowDeleteModal(false);
                setIsLoadingActions(false);
        }
    }, [newBookingsStatus]);

    useNonInitialEffect(() => {
        switch (bookingsStatus) {
            case BookingsStatus.Getting:
                setIsLoadingActions(true);
                break;
            case BookingsStatus.GetSuccess:
                setIsLoadingActions(false);
                break;
            case BookingsStatus.Error:
                setIsLoadingActions(false);
        }
    }, [newBookingsStatus]);

    useNonInitialEffect(() => {
        switch (orderStatus) {
            case OrdersStatus.Getting:
                setIsLoadingTaskComponent(true);
                break;
            case OrdersStatus.GetSuccess:
                setIsLoadingTaskComponent(false);
                break;
            case OrdersStatus.Error:
                setIsLoadingTaskComponent(false);
        }
    }, [orderStatus]);

    const clickHandler = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        if (props.previewActions || !props.task.appointmentTaskId) {
            return;
        }
        setPendingBookingDetail(true);
        dispatch(getAppointmentThunk(apiClientWrapper, props.task.appointmentTaskId));
    };

    useNonInitialEffect(() => {
        if (!pendingBookingDetail) {
            return;
        }
        if (appointmentsStatus === AppointmentsStatus.GetSuccess) {
            setPendingBookingDetail(false);
            setShowOrderDetail(true);
        } else if (appointmentsStatus === AppointmentsStatus.Error) {
            setPendingBookingDetail(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appointmentsStatus]);

    const handleCompleteTask = (event: MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        if (props.previewActions) {
            return;
        }
        if (props.task.status === 'completed') {
            dispatch(uncompleteTaskThunk(apiClientWrapper, props.task.id!));
        } else {
            dispatch(completeTaskThunk(apiClientWrapper, props.task.id!));
        }
    };

    const getTaskTime = () => {
        const date = props.task.taskDate?.clone().tz(timezone).format('MMM DD, YYYY');
        const feedingMoment = () => {
            switch (props.task.feeding?.feedingMomentTypeId) {
                case 1:
                    return 'Morning';
                case 2:
                    return 'Afternoon';
                default:
                    return 'Evening';
            }
        };
        const time = props.task.feeding ? feedingMoment() : props.task.taskDate?.tz(timezone).format('h:mm a');
        return (
            <>
                <div>{date}</div>
                <div>{time}</div>
            </>
        );
    };

    const deleteTask = () => {
        const taskToDelete: Task = {
            id: props.task.id,
            destroy: true
        };
        let tasksFormatted: Task[] = [...(appointment?.tasks ?? [])];
        tasksFormatted = tasksFormatted.filter(t => t.id !== props.task.id);
        tasksFormatted.push(taskToDelete);

        dispatch(
            singleRescheduleThunk(
                apiClientWrapper,
                appointment?.bookingId!,
                appointment?.id!,
                appointment?.duration?.from?.clone()!,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                false,
                undefined,
                tasksFormatted
            )
        );
    };

    const handleShowModalDelete = (event: MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        if (props.previewActions) {
            return;
        }
        setShowDeleteModal(true);
        dispatch(getAppointmentThunk(apiClientWrapper, props.task.appointmentTaskId!));
    };

    const handlerEditTask = (event: MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        if (props.previewActions) {
            return;
        }
        setIsLoadingTaskComponent(true);
        setTaskToEdit(props.task);
        setTaskModalKey(prev => prev + 1);
        setShowTaskComponent(true);
        dispatch(getOrderByAppointmentThunk(apiClientWrapper, props.task.appointmentTaskId!));
    };

    const handlerCloseTaskComponent = () => {
        setShowTaskComponent(false);
        setTaskToEdit(undefined);
        setSelectedTaskType(undefined);
    };

    const handlerAddTaskFromSuccess = () => {
        setTaskToEdit(undefined);
        setSelectedTaskType(undefined);
        setShowTaskComponent(false);
        setShowTaskTypeSelector(true);
    };

    const handlerSelectTaskType = (typeId: string) => {
        setSelectedTaskType(typeId);
        setShowTaskTypeSelector(false);
        setTaskModalKey(prev => prev + 1);
        setShowTaskComponent(true);
    };

    const notes =
        props.task.feeding?.feedingInstructions ||
        props.task.medication?.medicationInstructions ||
        careOf(props.task)?.instructions ||
        '-';
    const locationName = props.task.marketplaceId
        ? getOrganizationMarketplace(props.task.marketplaceId)?.name ?? '-'
        : '-';
    const allowActions = props.task.marketplaceId ? isUserLoggedInMarketplace(props.task.marketplaceId) : false;
    const showActions = props.previewActions || allowActions;
    // Add-on tasks (task_type 5) have no appointment and are read-only except complete/uncomplete.
    const canEdit = !!props.task.appointmentTaskId;
    const editActions = canEdit && (
        <>
            <div
                className={styles.actionIconContainer}
                onClick={handleShowModalDelete}
            >
                <FontAwesomeIcon
                    icon={faTrashCan}
                    className={styles.actionIcon}
                />
            </div>
            <div
                className={styles.actionIconContainer}
                onClick={handlerEditTask}
            >
                <FontAwesomeIcon
                    icon={faPen}
                    className={styles.actionIcon}
                />
            </div>
        </>
    );

    const pastDueChip = props.variant === 'past_due' && <div className={styles.pastDueChip}>Past Due</div>;

    const notMobileRow = (
        <TableRow
            className={clsx(styles.row, props.className)}
            onClick={clickHandler}
        >
            <TableCell className={styles.petNameCell}>
                <ListPetInfo
                    orderId={props.task.marketplaceId ?? 0}
                    pet={taskToPetListItem(props.task, masterData)}
                />
            </TableCell>
            <TableCell className={styles.taskCell}>
                <TaskInfo task={props.task} />
            </TableCell>
            <TableCell className={styles.dateCell}>
                {getTaskTime()} {pastDueChip}
            </TableCell>
            <TableCell className={styles.roomCell}>
                <div className={styles.roomName}>
                    {props.roomLabel ? `${props.roomLabel.room} · ${props.roomLabel.unit}` : '-'}
                </div>
                {props.showLocationColumn && props.roomLabel && (
                    <div className={styles.roomLocation}>
                        <FontAwesomeIcon
                            icon={faLocationDot}
                            className={styles.marketplaceIcon}
                        />
                        <span className={styles.locationText}>{locationName}</span>
                    </div>
                )}
            </TableCell>
            <TableCell className={styles.notesCell}>{notes}</TableCell>
            <TableCell className={styles.actionsCell}>
                {showActions && (
                    <div className={styles.actionsContainer}>
                        {editActions}
                        {isCompleted ? (
                            <div
                                className={styles.actionIconContainer}
                                onClick={handleCompleteTask}
                            >
                                <FontAwesomeIcon
                                    icon={faRotateLeft}
                                    className={styles.actionIcon}
                                />
                            </div>
                        ) : (
                            <FontAwesomeIcon
                                icon={faCircleCheck}
                                className={styles.completeTaskIcon}
                                onClick={handleCompleteTask}
                            />
                        )}
                    </div>
                )}
            </TableCell>
        </TableRow>
    );

    const mobileRow = (
        <TableRow
            className={clsx(styles.mobileRow, props.className)}
            onClick={clickHandler}
        >
            <div className={styles.mobileCard}>
                <div className={styles.mobileHeader}>
                    <ListPetInfo
                        orderId={props.task.marketplaceId ?? 0}
                        pet={taskToPetListItem(props.task, masterData)}
                    />
                    <span className={styles.mobileDate}>
                        {getTaskTime()} {pastDueChip}
                    </span>
                </div>
                <hr />
                <div className={styles.mobileTask}>
                    <TaskInfo task={props.task} />
                    {showActions && (
                        <div className={styles.actionsContainer}>
                            {editActions}
                            {isCompleted ? (
                                <div
                                    className={styles.actionIconContainer}
                                    onClick={handleCompleteTask}
                                >
                                    <FontAwesomeIcon
                                        icon={faRotateLeft}
                                        className={styles.actionIcon}
                                    />
                                </div>
                            ) : (
                                <FontAwesomeIcon
                                    icon={faCircleCheck}
                                    className={styles.completeTaskIcon}
                                    onClick={handleCompleteTask}
                                />
                            )}
                        </div>
                    )}
                </div>
                <hr />
                <div className={styles.mobileNotes}>
                    <strong>Room</strong>
                    <p>{props.roomLabel ? `${props.roomLabel.room} · ${props.roomLabel.unit}` : '-'}</p>
                    {props.showLocationColumn && props.roomLabel && (
                        <p className={styles.locationText}>{locationName}</p>
                    )}
                </div>
                <hr />
                <div className={styles.mobileNotes}>
                    <strong>Notes</strong>
                    <p>{notes}</p>
                </div>
            </div>
        </TableRow>
    );

    return (
        <>
            {isMobile ? mobileRow : notMobileRow}
            {showOrderDetail && appointment && (
                <OrderDetail
                    bookingId={appointment.bookingId}
                    orderId={appointment.orderId}
                    onClose={() => setShowOrderDetail(false)}
                />
            )}
            {showTaskTypeSelector && (
                <TaskTypeSelector
                    onSelectType={handlerSelectTaskType}
                    onClose={() => setShowTaskTypeSelector(false)}
                />
            )}
            {showTaskComponent && (
                <TaskComponent
                    key={taskModalKey}
                    task={taskToEdit}
                    appointments={order?.items?.appointments || []}
                    onClose={handlerCloseTaskComponent}
                    onAddTask={handlerAddTaskFromSuccess}
                    loading={isLoadingTaskComponent}
                    defaultTaskType={
                        taskToEdit
                            ? undefined
                            : (selectedTaskType as 'feeding' | 'medication' | 'relief' | 'hydration' | undefined)
                    }
                />
            )}
            {showDeleteModal && (
                <GenericDeleteModal
                    title={<span className={styles.title}>Delete Task</span>}
                    body={
                        <span className={styles.body}>
                            Are you sure you want to <strong className={styles.strong}>delete</strong> this Task?
                        </span>
                    }
                    onConfirm={deleteTask}
                    onCancel={() => setShowDeleteModal(false)}
                    isLoading={isLoadingActions}
                />
            )}
        </>
    );
};

export default TasksTableRow;
